/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { compose } from 'recompose';
import ReactMarkdown from 'react-markdown';
import { makeStyles } from '@material-ui/styles';
import { bindActionCreators } from 'redux';
import {
    object, shape, string, arrayOf, number, bool, func,
} from 'prop-types';
import { connect } from 'react-redux';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SubscriptionDropDown from './SubscriptionDropDown';
import { setUserConfirmationForSubscriptionPopup } from '../../../../../../state/ducks/App/App-Actions';
import SubscriptionOfferInformation from './SubscriptionOfferInformation';
import SubscriptionTnC from './SubscriptionTnC';
import Checkbox from './Checkbox';

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
    },
    subscriptionContainer: {
        display: 'flex',
        width: '100%',
        padding: '16px 23px',
        alignItems: 'center',
        '& .MuiCheckbox-root': {
            padding: 0,
            margin: '0 15px 0 0',
            height: 'fit-content',
        },
    },
    marginBottom15: {
        marginBottom: '15px',
    },
    subContainer: {
        display: 'flex',
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            '& > div': {
                width: '100%',
            },
        },
    },
    [theme.breakpoints.down('xs')]: {
        subContainerSm: {
            marginTop: 15,
        },
    },
    icon: {
        color: theme?.palette?.cms?.icon,
        margin: 'auto',
    },
    borderRight1: {
        [theme.breakpoints.up('sm')]: {
            borderRight: `1px solid ${(theme.palette.grey8)}`,
        },
    },
    marginTopBottom10: {
        margin: '10px 0px',
    },
    subcriptionContent: {
        padding: '0 50px',
        '& > div:nth-child(1)': {
            display: 'flex',
            '@media screen and (max-width: 832px)': {
                flexWrap: 'wrap',
            },
        },
        '& > p': {
            fontSize: 14,
            margin: '0 10px 10px',
        },
        '& #terms_and_conditions': {
            '& p': {
                fontSize: 14,
                margin: '0 10px 16px',
                [theme.breakpoints.down('600')]: {
                    fontSize: 12,
                },
                '& span': {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                },
            },
        },
    },
    fontSize67: {
        fontSize: 64,
        [theme.breakpoints.down('md')]: {
            fontSize: 45,
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: 35,
        },
        [theme.breakpoints.down('xs')]: {
            paddingLeft: 4,
            fontSize: 38,
        },

    },
    fontSize34: {
        fontSize: '34px',
    },
    subscriptionCheckBoxContent: {
        '& .single_line_copy': {
            fontSize: 16,
            '& p': {
                fontSize: 16,
                [theme.breakpoints.down('600')]: {
                    fontSize: 14,
                },
            },
        },
        '& .single_line_label': {
            fontSize: '18px !important',
            margin: 0,
            '& p': {
                fontSize: 18,
                [theme.breakpoints.down('600')]: {
                    fontSize: 16,
                },
            },
        },
    },
    checkbox: {
        color: theme.palette.cms?.checkbox,
        height: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
            marginTop: '5px',
            '& span svg': {
                fontSize: '24px',
            },
        },
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
        '&:hover': {
            backgroundColor: theme.palette.colorNeutral20,
        },
    },
    checked: {
        color: theme.palette.cms?.checkbox,
        '&$checked': {
            color: theme.palette.cms?.checkbox,
            '&:hover': {
                backgroundColor: theme.palette.colorNeutral20,
            },
        },
    },
    plaCheckbox: {
        [theme.breakpoints.up('1280')]: {
            width: '100%',
            padding: 0,
        },
    },
    marginNone: {
        margin: 0,
        '& p': {
            margin: 0,
        },
    },
    inline: {
        display: 'inline-block',
    },
    maxWidth: {
        maxWidth: '100%',
    },
    alignImage: {
        alignItems: 'center',
    },
    paddingLeft: {
        paddingLeft: '5px',
    },
    alignSpan: {
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },
    subscriptionDisplay: {
        display: 'none',
    },
    viewMore: ({ toggle }) => ({
        fontSize: '14px',
        textDecoration: 'underline',
        letterSpacing: '0.01em',
        backgroundColor: '#FAFAFA',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '8px 60px 11px',
        borderBottomRightRadius: toggle ? 0 : '3px',
        borderBottomLeftRadius: toggle ? 0 : '3px',
        [theme.breakpoints.down('600')]: {
            justifyContent: 'space-between',
            padding: '8px 22px 11px 60px',
            fontSize: 12,
        },
        '& > svg': {
            marginLeft: '10px',
        },
    }),
}));

const CollapsableSubscription = ({
    brand,
    subscriptionCopy,
    subscriptionDetailsFrequencyValue,
    subscriptionDetailsDurationValue,
    subscriptionConfig,
    isShowingFrequencyDropdown,
    isSubscriptionDurationListEnabled,
    selectValue,
    selectedSku,
    handleChange,
    durationValue,
    subscriptionCheckbox,
    triggerSubscriptionCheck,
    trackEvent,
    handleUserConfirmation,
    pdpOfferContent,
}) => {
    const isSmallDevice = useMediaQuery((theme) => theme?.breakpoints.down('sm'));
    const hasIcon = subscriptionCopy?.icon?.url || null;
    const [toggle, setToggle] = useState(false);
    const classes = useStyles({ toggle });
    const [open, setOpen] = useState(false);

    const handleModal = (e, windowOpen) => {
        e.nativeEvent?.stopImmediatePropagation();
        e.preventDefault();
        e.persist();
        e.stopPropagation();
        setOpen(windowOpen);

        if (windowOpen) {
            trackEvent({
                eventCategory: 'Subscriptions PDP Modal',
                eventAction: 'click',
                eventLabel: 'T&Cs',
            });
        }
    };

    const userConfirmation = () => {
        handleUserConfirmation(true);
        setOpen(false);
    };

    const handleToggle = (flag) => {
        setToggle(flag);
        trackEvent({
            eventCategory: 'Subscriptions PDP Modal',
            eventAction: 'click',
            eventLabel: flag ? 'Details' : 'Close',
        });
    };

    const renderSingleLineCopy = () => {
        if (subscriptionCopy?.single_line_copy?.toLowerCase?.().trim?.() === 'n/a') {
            return '';
        }
        if (subscriptionCopy?.single_line_copy) {
            return <ReactMarkdown className={`${classes.marginNone} single_line_copy`} source={subscriptionCopy?.single_line_copy} />;
        }
        return <p className={`${classes.marginNone} single_line_copy`}>Set your frequency and duration. Skip, pause, or cancel anytime.</p>;
    };
    const renderSubscriptionDropDown = () => (
        <div className={classes.subcriptionContent}>
            <div>
                <SubscriptionDropDown
                    selectValue={selectValue}
                    subscriptionsList={selectedSku.subscriptionsList}
                    fieldLabel={subscriptionCopy?.frequency_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.frequency_label} /> : 'Frequency:'}
                    handleChange={handleChange}
                    defaultValue={subscriptionConfig.subscription_interval[subscriptionDetailsFrequencyValue] || subscriptionDetailsFrequencyValue}
                    isShowingDropdown={isShowingFrequencyDropdown}
                />
                <SubscriptionDropDown
                    selectValue={durationValue}
                    subscriptionsList={selectedSku.subscriptionsList}
                    handleChange={handleChange}
                    fieldLabel={subscriptionCopy?.duration_label ? <ReactMarkdown className={`${classes.marginNone} ${classes.inline}`} source={subscriptionCopy?.duration_label} /> : 'Duration:'}
                    dropdownType="subscription_duration"
                    frequencyOrDuration="duration"
                    defaultValue={subscriptionConfig.subscription_duration[subscriptionDetailsDurationValue] || subscriptionDetailsDurationValue}
                    isShowingDropdown={isSubscriptionDurationListEnabled}
                />
            </div>
            <SubscriptionTnC brand={brand} copy={pdpOfferContent.terms_and_conditions} userConfirmation={userConfirmation} handleModal={handleModal} open={open}  />
        </div>
    );
    return (
        <div className={classes.container}>
            <div container className={classes.subscriptionContainer}>
                <div>
                    <Checkbox
                        id="chk-monthly-subscription"
                        data-test="pdp-check-monthly-subscription"
                        data-testid="pdp-check-monthly-subscription"
                        name="monthlySubscriptionChecked"
                        checked={subscriptionCheckbox}
                        onChange={triggerSubscriptionCheck}
                    />
                    {isSmallDevice && hasIcon && (
                        <img className={classes.maxWidth} src={subscriptionCopy?.icon?.url} alt="subscription-icon" />
                    )}
                </div>
                <div className={`${classes.subContainer} ${classes.alignSpan}`}>
                    <div className={`${classes.subscriptionCheckBoxContent}`}>
                        {subscriptionCopy?.single_line_label ? <ReactMarkdown className={`${classes.marginNone} single_line_label`} source={subscriptionCopy?.single_line_label} /> : (<p className="single_line_label"><strong>Make this a recurring Shipment</strong><br /></p>)}
                        {renderSingleLineCopy()}
                    </div>
                </div>
            </div>
            {subscriptionCheckbox && renderSubscriptionDropDown()}
            <div className={classes.viewMore} onKeyDown={(e) => e.key === 'Enter' && handleToggle(!toggle)} role="button" tabIndex={0} onClick={() => handleToggle(!toggle)}>
                <ReactMarkdown className={classes.marginNone} source={pdpOfferContent?.offer_content?.offer_title} /> {!toggle ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </div>
            {/* {toggle && ( */}
            <SubscriptionOfferInformation
                copy={pdpOfferContent?.offer_content || {}}
                handleModal={handleModal}
                toggle={toggle}
            />
            {/* )} */}
        </div>
    );
};

CollapsableSubscription.propTypes = {
    subscriptionCopy: shape({
        single_line_label: string,
        frequency_label: string,
        duration_label: string,
    }),
    subscriptionDetailsFrequencyValue: number.isRequired,
    subscriptionDetailsDurationValue: number.isRequired,
    subscriptionConfig: shape({
        subscription_duration: arrayOf(number),
        subscription_interval: arrayOf(number),
    }).isRequired,
    isShowingFrequencyDropdown: bool,
    brand: shape({
        domain: string,
    }).isRequired,

    isSubscriptionDurationListEnabled: bool.isRequired,
    selectValue: string.isRequired,
    selectedSku: shape({
        subscriptionsList: shape({
            type: arrayOf(string),
        }),
    }).isRequired,
    handleChange: func.isRequired,
    durationValue: string.isRequired,
    subscriptionCheckbox: bool.isRequired,
    triggerSubscriptionCheck: func.isRequired,
    trackEvent: func.isRequired,
    handleUserConfirmation: func.isRequired,
    pdpOfferContent: object.isRequired,
};

CollapsableSubscription.defaultProps = {
    subscriptionCopy: {},
    isShowingFrequencyDropdown: false,
};

const mapDispatchToProps = (dispatch) => ({
    handleUserConfirmation: bindActionCreators(setUserConfirmationForSubscriptionPopup, dispatch),
});

const enhance = compose(
    connect(null, mapDispatchToProps),
);

export default enhance(CollapsableSubscription);
